import React, { useState, useEffect } from 'react';
import { platformUsersApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Users, Plus, Pencil, Trash, ShieldCheck, Headset, CurrencyCircleDollar, Wrench, Crown,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const PLATFORM_ROLES = [
  { value: 'super_admin', label: 'Super Admin', icon: Crown, desc: 'Full platform access', color: 'text-violet-600 bg-violet-50' },
  { value: 'platform_admin', label: 'Platform Admin', icon: ShieldCheck, desc: 'Full platform access', color: 'text-indigo-600 bg-indigo-50' },
  { value: 'support_team', label: 'Support Team', icon: Headset, desc: 'Ticket management only', color: 'text-teal-600 bg-teal-50' },
  { value: 'account_team', label: 'Account Team', icon: CurrencyCircleDollar, desc: 'Hospitals, Plans, Billing', color: 'text-amber-600 bg-amber-50' },
  { value: 'technical_team', label: 'Technical Team', icon: Wrench, desc: 'ABDM, Security, Settings', color: 'text-blue-600 bg-blue-50' },
];

const getRoleConfig = (role) => PLATFORM_ROLES.find(r => r.value === role) || PLATFORM_ROLES[2];

const PlatformUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', role: 'support_team', password: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try { const res = await platformUsersApi.list(); setUsers(res.data || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditUser(null);
    setForm({ name: '', email: '', role: 'support_team', password: '' });
    setDialog(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ name: u.name, email: u.email, role: u.role, password: '' });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    if (!editUser && !form.password) { toast.error('Password is required for new users'); return; }
    setSaving(true);
    try {
      if (editUser) {
        const data = { name: form.name, role: form.role };
        if (form.password) data.password = form.password;
        await platformUsersApi.update(editUser.id, data);
        toast.success('User updated');
      } else {
        await platformUsersApi.create(form);
        toast.success('User created');
      }
      setDialog(false);
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (u) => {
    try {
      await platformUsersApi.update(u.id, { is_active: !u.is_active });
      toast.success(`User ${u.is_active ? 'deactivated' : 'activated'}`);
      loadUsers();
    } catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try {
      await platformUsersApi.delete(u.id);
      toast.success('User deleted');
      loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="platform-users-page">
      <Toaster position="top-right" richColors />

      <div className="bg-gradient-to-r from-violet-900 via-slate-900 to-indigo-900 rounded-2xl p-6 text-white" data-testid="platform-users-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-violet-500/20 flex items-center justify-center border border-violet-500/30">
              <Users className="h-6 w-6 text-violet-400" weight="duotone" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-['Outfit']">Platform Team</h1>
              <p className="text-sm text-slate-300 mt-0.5">Manage platform users with role-based access</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-violet-500 hover:bg-violet-600 text-white gap-1.5 text-sm" data-testid="add-user-btn">
            <Plus className="h-4 w-4" /> Add Team Member
          </Button>
        </div>
      </div>

      {/* Role Legend */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {PLATFORM_ROLES.map(r => (
          <Card key={r.value} className={`border-0 shadow-sm ${r.color.split(' ')[1]}`}>
            <CardContent className="p-3 flex items-center gap-2">
              <r.icon className={`h-5 w-5 ${r.color.split(' ')[0]}`} weight="duotone" />
              <div>
                <p className="text-xs font-semibold text-slate-700">{r.label}</p>
                <p className="text-[9px] text-slate-500">{r.desc}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Users Table */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="users-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" /></div>
          ) : users.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-sm">No platform users found</div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Name</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Email</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Role</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-center">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {users.map(u => {
                  const rc = getRoleConfig(u.role);
                  return (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center ${rc.color.split(' ')[1]}`}>
                            <rc.icon className={`h-4 w-4 ${rc.color.split(' ')[0]}`} />
                          </div>
                          <span className="text-sm font-medium text-slate-800">{u.name}</span>
                        </div>
                      </TableCell>
                      <TableCell><span className="text-xs text-slate-600">{u.email}</span></TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${rc.color.split(' ')[1]} ${rc.color.split(' ')[0]} border-0 text-[10px]`}>{rc.label}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`border-0 text-[10px] cursor-pointer ${u.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
                          onClick={() => handleToggleActive(u)}>
                          {u.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500" onClick={() => openEdit(u)} data-testid={`edit-${u.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(u)} data-testid={`delete-${u.id}`}>
                            <Trash className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-['Outfit']">{editUser ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="user-name-input" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} disabled={!!editUser} data-testid="user-email-input" /></div>
            <div><Label>Role *</Label>
              <Select value={form.role} onValueChange={v => setForm(f => ({...f, role: v}))}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PLATFORM_ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label} — {r.desc}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{editUser ? 'New Password (leave blank to keep)' : 'Password *'}</Label>
              <Input type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))}
                placeholder={editUser ? 'Leave blank to keep current' : 'Min 8 chars, uppercase, lowercase, number, special'} data-testid="user-password-input" />
            </div>
            <Button className="w-full bg-gradient-to-r from-violet-500 to-indigo-600 text-white" onClick={handleSave} disabled={saving} data-testid="save-user-btn">
              {saving ? 'Saving...' : editUser ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlatformUsersPage;
